import React, { PropsWithChildren, createContext, useEffect, useLayoutEffect, useState } from 'react';
import { KioskTheme, KioskThemes } from '../../css/themes/kioskthemes';
import { useKioskState, useMonochromeTheme, useNoAnimations } from '../Kiosk/kiosk';
import { isDevelopment, isElectron } from '../../helpers/EnvironmentFlags/EnvironmentFlags';
import css from './kioskTheme.module.scss';
import { UseKioskThemeFunction } from './kioskTheme.types';
import { GMDevToolSettings } from '../../controls/DevTools';

// TODO this doesn't actually change the favicon when switching to a secondary dealer
const setFavicon = (src: string) => {
    const existingFavicon: Element | null = document.querySelector(`link[href~="${src}"]`);

    if (existingFavicon !== null) {
        const link = document.createElement('link');
        link.type = 'image/x-icon';
        link.rel = 'shortcut icon';
        link.href = src;
        document.getElementsByTagName('head')[0].appendChild(link);
    }
};

const getKioskTheme = (theme: string): KioskTheme => {
    return KioskThemes[theme || 'default'] || KioskThemes['default'];
};

const KioskThemeStateContext = createContext<KioskTheme | undefined>(undefined);

export const KioskThemeProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const {
        dealer: { theme },
    } = useKioskState();
    const monochromeTheme = useMonochromeTheme();
    const noAnimations = useNoAnimations();

    const [state, setState] = useState<KioskTheme>(getKioskTheme(theme));

    useLayoutEffect(() => {
        const themeClasses: string[] = Array.from(document.body.classList)
            .filter((i) => (/^theme-(?!(monochrome|no-animations))/).test(i));

        /*
         * Only add theme to body if the dealer theme is present or we haven't loaded a theme yet.
         * This will allow the default theme to be present while fetching the kiosk information and
         * retain the previously fetched kiosk theme when the kiosk information is blown out when returning to the
         * home screen
         */
        if (theme || themeClasses.length === 0) {
            themeClasses.forEach((i) => document.body.classList.remove(i));

            const newTheme: KioskTheme = getKioskTheme(theme);
            document.body.classList.add(`theme-${newTheme.name}`);

            setState(newTheme);
            setFavicon(newTheme.favicon);
        }
    }, [theme]);

    useEffect(() => {
        document.body.classList[monochromeTheme ? 'add' : 'remove']('theme-monochrome');
        document.body.classList[noAnimations ? 'add' : 'remove']('theme-no-animations');
    }, [monochromeTheme, noAnimations]);

    return (
        <KioskThemeStateContext.Provider value={state}>
            <div id={'appSetter'} className={`${css.app} ${isElectron() ? 'kiosk' : 'web'}`}>
                {children}
                {isDevelopment() ? (
                    <GMDevToolSettings />
                ) : null}
            </div>
        </KioskThemeStateContext.Provider>
    );
};

export const useKioskTheme: UseKioskThemeFunction = () => {
    const context: KioskTheme | undefined = React.useContext(KioskThemeStateContext);

    if (!context) {
        throw new Error('useKioskTheme must be used within a KioskThemeProvider');
    }

    return context;
};